import { Helmet } from "react-helmet-async";
import { BRAND, SOCIAL } from "@/lib/content";

const SITE_NAME = "StudyPoland.info";
const SITE_URL = "https://studypoland.info";
const DEFAULT_TITLE = "StudyPoland — Expert guidance for international students in Poland";
const DEFAULT_DESC =
  "Helping international students and families choose the right university in Poland and move through admissions, visa, and relocation with clarity. 15+ years of expert support across 100+ Polish universities.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

/**
 * Per-page SEO component.
 *
 * Props:
 *   - title: page-specific title; site name appended automatically
 *   - description: meta description (max ~160 chars)
 *   - path: canonical path (default current pathname)
 *   - image: og:image absolute URL
 *   - type: og:type, default "website"; use "article" for blog posts
 *   - jsonLd: optional JSON-LD structured-data object (or array)
 *   - noIndex: set true on admin/private routes
 */
export default function SEO({
  title,
  description = DEFAULT_DESC,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = `${SITE_URL}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;

  const renderJsonLd = (data) =>
    data ? (
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    ) : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article author */}
      <meta name="author" content={BRAND.expertName} />

      {/* JSON-LD */}
      {Array.isArray(jsonLd)
        ? jsonLd.map((d, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(d)}
            </script>
          ))
        : renderJsonLd(jsonLd)}
    </Helmet>
  );
}

// ---------- Reusable JSON-LD builders ----------

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: SITE_NAME,
  alternateName: ["StudyPoland", "Marhaba Poland"],
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: DEFAULT_DESC,
  founder: {
    "@type": "Person",
    name: BRAND.expertName,
    jobTitle: BRAND.expertTitle,
  },
  email: BRAND.email,
  telephone: BRAND.whatsappDisplay,
  areaServed: "Poland",
  sameAs: [SOCIAL.facebook, SOCIAL.tiktok],
};

export const PERSON_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: BRAND.expertName,
  jobTitle: BRAND.expertTitle,
  description:
    "International students advisor with 15+ years working with Polish higher education institutions.",
  url: `${SITE_URL}/about`,
  worksFor: { "@type": "Organization", name: SITE_NAME },
  sameAs: [SOCIAL.facebook, SOCIAL.tiktok],
};

export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function articleJsonLd(post) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt || "",
    image: post.cover_image || DEFAULT_OG_IMAGE,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: {
      "@type": "Person",
      name: BRAND.expertName,
      url: `${SITE_URL}/about`,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: `${SITE_URL}/insights/${post.slug}`,
  };
}

export function breadcrumbJsonLd(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.path}`,
    })),
  };
}
